import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import { Money } from "./badges";
import { num } from "../lib/format";
import { ChevronRight, AlertTriangle, Search, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";

const empId = (e) => e.provider_employee_id || e.provider_file_number_as_printed || "";

const COLS = [
  { key: "employee_name_as_printed", label: "Employee", type: "str" },
  { key: "emp_id", label: "Emp ID", type: "str" },
  { key: "department_as_printed", label: "Department", type: "str" },
  { key: "total_hours", label: "Hours", type: "num", right: true },
  { key: "gross_pay_amount", label: "Gross", type: "num", right: true },
  { key: "net_pay_amount", label: "Net", type: "num", right: true },
  { key: "issue_count", label: "Issues", type: "num", right: true },
];

const matchesQuery = (e, query) =>
  !query ||
  [e.employee_name_as_printed, empId(e), e.department_as_printed].some(
    (v) => v && String(v).toLowerCase().includes(query)
  );

const makeComparator = (sort, col) => {
  const val = (e) => (sort.key === "emp_id" ? empId(e) : e[sort.key]);
  return (a, b) => {
    let av = val(a);
    let bv = val(b);
    if (col?.type === "num") {
      av = av ?? 0;
      bv = bv ?? 0;
      return sort.dir === "asc" ? av - bv : bv - av;
    }
    av = (av || "").toString().toLowerCase();
    bv = (bv || "").toString().toLowerCase();
    return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  };
};

export function EmployeesTable({ employees, ccy, runId }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "issue_count", dir: "desc" });
  const [issuesOnly, setIssuesOnly] = useState(() => localStorage.getItem("pd_issues_only") === "1");

  const toggleIssuesOnly = () => {
    setIssuesOnly((v) => {
      localStorage.setItem("pd_issues_only", v ? "0" : "1");
      return !v;
    });
  };

  const toggle = (key, type) =>
    setSort((s) => (s.key === key
      ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
      : { key, dir: type === "num" ? "desc" : "asc" }));

  const rows = useMemo(() => {
    const query = q.trim().toLowerCase();
    const col = COLS.find((c) => c.key === sort.key);
    return employees
      .filter((e) => !issuesOnly || e.issue_count > 0)
      .filter((e) => matchesQuery(e, query))
      .slice()
      .sort(makeComparator(sort, col));
  }, [employees, q, sort, issuesOnly]);

  const SortIcon = ({ k }) =>
    sort.key !== k ? <ChevronsUpDown size={13} className="opacity-40" />
      : sort.dir === "asc" ? <ArrowUp size={13} /> : <ArrowDown size={13} />;

  const flaggedCount = employees.filter((e) => e.issue_count > 0).length;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="employee-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, Emp ID, department…"
            className="w-full rounded-full border border-border bg-card py-1.5 pl-8 pr-3 text-sm outline-none focus:border-foreground/40"
          />
        </div>
        <button
          data-testid="issues-only-toggle"
          onClick={toggleIssuesOnly}
          aria-pressed={issuesOnly}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${
            issuesOnly ? "border-neg bg-neg/15 text-neg" : "border-border text-muted-foreground hover:text-foreground"
          }`}
        >
          <AlertTriangle size={14} /> Issues only
          <span className="font-mono text-xs opacity-80">({num(flaggedCount)})</span>
        </button>
        <span data-testid="employee-count" className="ml-auto text-xs text-muted-foreground">
          Showing <span className="font-mono text-foreground">{num(rows.length)}</span> of {num(employees.length)}
        </span>
      </div>
      <div className="rounded-lg border border-border bg-card overflow-auto max-h-[36rem]">
        <Table className="tbl-sticky">
          <TableHeader>
            <TableRow className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              {COLS.map((c) => (
                <TableHead key={c.key} className={c.right ? "text-right" : ""}>
                  <button
                    data-testid={`sort-${c.key}`}
                    onClick={() => toggle(c.key, c.type)}
                    className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wider transition-colors hover:text-foreground ${c.right ? "flex-row-reverse" : ""} ${sort.key === c.key ? "text-foreground" : ""}`}
                  >
                    {c.label} <SortIcon k={c.key} />
                  </button>
                </TableHead>
              ))}
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((e) => (
              <TableRow
                key={e.payroll_run_employee_GUID}
                data-testid={`employee-row-${e.payroll_employee_GUID}`}
                className={`hover:bg-card-hover border-l-2 ${e.issue_count > 0 ? "border-l-neg bg-neg/[0.03]" : "border-l-transparent"}`}
              >
                <TableCell className="font-medium">{e.employee_name_as_printed}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{empId(e) || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{e.department_as_printed || "—"}</TableCell>
                <TableCell className="text-right font-mono">{num(e.total_hours, 2)}</TableCell>
                <TableCell className="text-right"><Money value={e.gross_pay_amount} ccy={ccy} /></TableCell>
                <TableCell className="text-right"><Money value={e.net_pay_amount} ccy={ccy} /></TableCell>
                <TableCell className="text-right">
                  {e.issue_count > 0 ? (
                    <span className="inline-flex items-center gap-1 font-mono font-bold text-neg">
                      <AlertTriangle size={13} /> {num(e.issue_count)}
                    </span>
                  ) : (
                    <span className="font-mono text-pos">0</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Link
                    to={`/runs/${runId}/employees/${e.payroll_employee_GUID}`}
                    data-testid={`employee-link-${e.payroll_employee_GUID}`}
                    className="inline-flex items-center text-sm font-semibold text-muted-foreground hover:text-foreground"
                  >
                    Detail <ChevronRight size={16} />
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                  No employees match your search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
